import { useState, useMemo } from "react";
import { toast } from "sonner";
import {
    RocketLaunch,
    ArrowsClockwise,
    Sparkle,
    X,
    Plus,
    Check,
    Target as TargetIcon,
    Prohibit,
    Warning,
    ArrowRight,
    ArrowLeft,
    MapPin,
    Buildings,
    Money,
    Link as LinkIcon,
    ArrowSquareOut,
    Trash,
    CaretDown,
} from "@phosphor-icons/react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    campaignBuilderEnrich,
    campaignBuilderPlan,
    campaignBuilderPush,
    campaignBuilderSuggestNegatives, errText,
} from "@/lib/api";
import {
    filterKeywordHygiene,
    buildStandardNegatives,
    HYGIENE_REASON_LABELS,
} from "@/lib/keywordHygiene";

const STEPS = [
    { key: "config", label: "Config" },
    { key: "enrich", label: "Enrich" },
    { key: "plan", label: "Plan" },
    { key: "confirm", label: "Confirm" },
    { key: "done", label: "Done" },
];

const parseChips = (t) =>
    t
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);

const SOURCE_BADGES = {
    seed: { label: "Seed", cls: "bg-neutral-900 text-white" },
    related: { label: "Related", cls: "bg-blue-100 text-blue-900 border border-blue-300" },
    near_me: { label: "Near-me", cls: "bg-emerald-100 text-emerald-900 border border-emerald-300" },
    city: { label: "City", cls: "bg-klein/10 text-klein border border-klein" },
    area: { label: "Area", cls: "bg-purple-100 text-purple-900 border border-purple-300" },
    service: { label: "Service", cls: "bg-amber-100 text-amber-900 border border-amber-300" },
    competitor: { label: "Competitor", cls: "bg-red-100 text-red-900 border border-red-300" },
};

export const CampaignPushWizard = ({
    open,
    onOpenChange,
    targets,        // [{keyword, ...}]
    negatives,      // [{keyword, ...}]
    country,
    language,
    city,           // string label
    cityGeoId,      // numeric geo target constant id (needed for push)
    languageId,     // "1000" etc (from KP options mapping)
    customerId,
    accountName,
}) => {
    const [step, setStep] = useState("config");

    // Step 1 — config
    const [businessName, setBusinessName] = useState("");
    const [businessType, setBusinessType] = useState("");
    const [websiteUrl, setWebsiteUrl] = useState("");
    const [dailyBudget, setDailyBudget] = useState("");
    const [competitorText, setCompetitorText] = useState("");
    const [areasText, setAreasText] = useState("");
    const [servicesText, setServicesText] = useState("");
    const [painPoints, setPainPoints] = useState("");
    const [focusThreeWord, setFocusThreeWord] = useState(true);
    const [customNegatives, setCustomNegatives] = useState(""); // user-typed extra negatives
    const [suggesting, setSuggesting] = useState(false);
    const [suggestedNegatives, setSuggestedNegatives] = useState([]); // [{keyword, category}]
    const [suggestedGrouped, setSuggestedGrouped] = useState({});    // {category: [kw, ...]}

    // Step 2 — enrichment result
    const [enriching, setEnriching] = useState(false);
    const [enriched, setEnriched] = useState(null); // {keywords, sources, total, three_word_count}
    const [droppedKws, setDroppedKws] = useState(new Set());
    const [autoClean, setAutoClean] = useState(true); // strip rival brands / SKUs / info
    const [appendStdNegatives, setAppendStdNegatives] = useState(true); // auto-append standard negatives

    // Step 3 — plan
    const [planning, setPlanning] = useState(false);
    const [plan, setPlan] = useState(null); // {plan: {campaign_name, ad_groups[]}, negatives[], keyword_count, ad_group_count}
    const [expandedGroups, setExpandedGroups] = useState(new Set([0]));

    // Step 5 — result
    const [pushing, setPushing] = useState(false);
    const [pushResult, setPushResult] = useState(null);
    const [pushError, setPushError] = useState("");
    const [doneMode, setDoneMode] = useState("csv"); // "csv" | "push"

    // Reset when the modal is (re-)opened.
    const reset = () => {
        setStep("config");
        setEnriched(null);
        setDroppedKws(new Set());
        setPlan(null);
        setPushResult(null);
        setPushError("");
        setDoneMode("csv");
        setCustomNegatives("");
        setSuggestedNegatives([]);
        setSuggestedGrouped({});
    };

    const handleSuggestNegatives = async () => {
        if (!businessName.trim() || !city) {
            toast.error("Fill business name (and pick a city) before suggesting negatives");
            return;
        }
        setSuggesting(true);
        const t = toast.loading("Asking Claude for a curated negatives list…");
        try {
            const data = await campaignBuilderSuggestNegatives({
                business_name: businessName.trim(),
                business_type: (businessType || businessName).trim(),
                city,
                country: country === "GB" ? "United Kingdom" :
                        country === "US" ? "United States" :
                        country === "IN" ? "India" :
                        country === "CA" ? "Canada" :
                        country === "AU" ? "Australia" : country,
            });
            setSuggestedNegatives(data.negatives || []);
            setSuggestedGrouped(data.grouped || {});
            toast.success(
                `Got ${data.count || 0} suggested negatives across ${Object.keys(data.grouped || {}).length} categories`,
                { id: t }
            );
        } catch (e) {
            const msg =
                errText(e, "Suggest-negatives failed");
            toast.error(msg, { id: t });
        } finally {
            setSuggesting(false);
        }
    };

    // Rival brand list = competitor domain roots + anything the Distiller
    // saved to localStorage after its last successful run (Claude's expanded
    // list). Kept lower-cased and de-duped.
    const rivalBrands = useMemo(() => {
        const out = new Set();
        // Competitor URLs → strip protocol/www + take domain root.
        for (const raw of parseChips(competitorText)) {
            try {
                const u = raw.startsWith("http") ? new URL(raw) : new URL("http://" + raw);
                const host = u.hostname.replace(/^www\./, "");
                const root = host.split(".")[0];
                if (root && root.length > 2) out.add(root.toLowerCase());
            } catch {
                const t = raw.replace(/^https?:\/\//i, "").split(/[./]/)[0];
                if (t && t.length > 2) out.add(t.toLowerCase());
            }
        }
        // Distiller's expanded brand list (persisted across dialog lifetimes).
        if (typeof window !== "undefined") {
            try {
                const j = window.localStorage.getItem("mb_distiller_brands");
                if (j) {
                    JSON.parse(j).forEach((b) => {
                        const s = (b || "").toString().toLowerCase().trim();
                        if (s && s.length > 1) out.add(s);
                    });
                }
            } catch {
                /* ignore */
            }
        }
        return Array.from(out);
    }, [competitorText]);

    // Combined negatives = KP-selected + Claude-suggested + user-typed +
    // (optional) standard blocklist + rival brand names.
    const combinedNegatives = useMemo(() => {
        const seen = new Set();
        const out = [];
        const push = (kw) => {
            const s = (kw || "").trim().toLowerCase();
            if (!s || seen.has(s)) return;
            seen.add(s);
            out.push(s);
        };
        (negatives || []).forEach((n) => push(n.keyword));
        suggestedNegatives.forEach((n) => push(n.keyword));
        parseChips(customNegatives).forEach(push);
        if (appendStdNegatives) {
            buildStandardNegatives(rivalBrands).forEach(push);
        }
        return out;
    }, [negatives, suggestedNegatives, customNegatives, appendStdNegatives, rivalBrands]);

    // ---- Actions ----
    const handleEnrich = async () => {
        if (!businessName.trim() || !websiteUrl.trim() || !dailyBudget) {
            toast.error("Fill business name, website URL, and daily budget first");
            return;
        }
        setEnriching(true);
        const t = toast.loading("Enriching keywords…");
        try {
            const seeds = targets.map((k) => k.keyword);
            const data = await campaignBuilderEnrich({
                seeds,
                city,
                country,
                language,
                areas: parseChips(areasText),
                services: parseChips(servicesText),
                competitor_urls: parseChips(competitorText),
                include_near_me: true,
                include_city_variants: !!city,
                dfs_related: true,
                focus_word_count: 3,
            });
            setEnriched(data);
            setDroppedKws(new Set());
            setStep("enrich");
            toast.success(
                `Enriched to ${data.total} keywords · ${data.three_word_count} are 3-word`,
                { id: t }
            );
        } catch (e) {
            const msg = errText(e, "Enrichment failed");
            toast.error(msg, { id: t });
        } finally {
            setEnriching(false);
        }
    };

    // ---- Apply keyword-hygiene filter over the enriched list (reactive) ----
    const hygiene = useMemo(() => {
        if (!enriched?.keywords) {
            return { kept: [], dropped: [], reasons: {} };
        }
        if (!autoClean) {
            return { kept: enriched.keywords, dropped: [], reasons: {} };
        }
        return filterKeywordHygiene(enriched.keywords, {
            rivalBrands,
        });
    }, [enriched, autoClean, rivalBrands]);

    const activeKeywords = useMemo(() => {
        if (!enriched) return [];
        let list = hygiene.kept;
        if (focusThreeWord) list = list.filter((k) => k.word_count === 3);
        list = list.filter((k) => !droppedKws.has(k.keyword));
        return list;
    }, [enriched, hygiene, focusThreeWord, droppedKws]);

    const dropKw = (k) => setDroppedKws((prev) => new Set(prev).add(k));

    const handlePlan = async () => {
        if (activeKeywords.length < 3) {
            toast.error("Keep at least 3 keywords to cluster into ad groups");
            return;
        }
        setPlanning(true);
        const t = toast.loading("Clustering into ad groups + generating ads…");
        try {
            const data = await campaignBuilderPlan({
                business_name: businessName.trim(),
                business_type: businessType.trim() || businessName.trim(),
                website_url: websiteUrl.trim(),
                city,
                services: parseChips(servicesText),
                areas: parseChips(areasText),
                pain_points: painPoints.trim(),
                keywords: activeKeywords.map((k) => k.keyword),
                negatives: combinedNegatives,
            });
            setPlan(data);
            setExpandedGroups(new Set([0]));
            setStep("plan");
            toast.success(
                `${data.ad_group_count} ad groups · ${data.keyword_count} keywords`,
                { id: t }
            );
        } catch (e) {
            const msg = errText(e, "Planning failed");
            toast.error(msg, { id: t });
        } finally {
            setPlanning(false);
        }
    };

    const handlePush = async () => {
        if (!cityGeoId) {
            toast.error(
                "No city geo target selected — go back to Keyword Planner and add a city in the geo picker"
            );
            return;
        }
        let url = websiteUrl.trim();
        if (!/^https?:\/\//i.test(url)) url = "https://" + url;
        setPushing(true);
        setPushError("");
        const t = toast.loading("Pushing to Google Ads (all PAUSED)…");
        try {
            const data = await campaignBuilderPush({
                customer_id: customerId,
                plan: plan.plan,
                negatives: plan.negatives || [],
                daily_budget: Number(dailyBudget),
                city_geo_id: String(cityGeoId),
                language_id: String(languageId || "1000"),
                final_url: url,
            });
            setPushResult(data);
            setDoneMode("push");
            setStep("done");
            toast.success("Pushed! Campaign created in PAUSED state.", { id: t });
        } catch (e) {
            const msg = errText(e, "Push failed");
            setPushError(msg);
            toast.error(msg, { id: t });
        } finally {
            setPushing(false);
        }
    };

    // ---- Google Ads Editor CSV fallback ----
    // Emitted while the developer token is still on Explorer/Test access. The
    // format mirrors the "Import CSV" schema Google Ads Editor understands.
    const downloadEditorCsv = () => {
        if (!plan?.plan) return;
        const p = plan.plan;
        const budget = String(dailyBudget || "");
        const cityLabel = city || "";
        const rows = [];
        // Header row — Google Ads Editor is header-agnostic for these keys.
        rows.push([
            "Campaign", "Campaign type", "Budget", "Networks", "Location", "Language",
            "Campaign status",
            "Ad Group", "Ad Group status",
            "Keyword", "Match type", "Keyword status",
            "Final URL",
            "Headline 1", "Headline 2", "Headline 3",
            "Headline 4", "Headline 5", "Headline 6",
            "Description 1", "Description 2", "Description 3",
        ]);
        let url = websiteUrl.trim();
        if (url && !/^https?:\/\//i.test(url)) url = "https://" + url;
        p.ad_groups.forEach((g) => {
            const h = (g.ad?.headlines || []).map((x) => x.text || "");
            while (h.length < 6) h.push("");
            const d = (g.ad?.descriptions || []).map((x) => x || "");
            while (d.length < 3) d.push("");
            // One row per keyword × match type (Phrase + Exact), first row also
            // carries the RSA so Editor pairs it with the ad group.
            let first = true;
            (g.keywords || []).forEach((kw) => {
                ["Phrase", "Exact"].forEach((match) => {
                    rows.push([
                        p.campaign_name,
                        "Search",
                        budget,
                        "Google search",
                        cityLabel,
                        "en",
                        "Paused",
                        g.name,
                        "Paused",
                        kw,
                        match,
                        "Paused",
                        url,
                        first ? h[0] : "",
                        first ? h[1] : "",
                        first ? h[2] : "",
                        first ? h[3] : "",
                        first ? h[4] : "",
                        first ? h[5] : "",
                        first ? d[0] : "",
                        first ? d[1] : "",
                        first ? d[2] : "",
                    ]);
                    first = false;
                });
            });
        });
        // Emit + trigger download.
        const escape = (v) => {
            const s = v == null ? "" : String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = rows.map((r) => r.map(escape).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const dl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const baseName = (p.campaign_name || "campaign").replace(/[^\w-]+/g, "_");
        const filename = `${baseName}.csv`;
        a.href = dl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(dl);

        // ---- Second file: negatives CSV in Google Ads Editor format ----
        // Columns match the attached example: Campaign, Ad Group, Keyword, Criterion Type.
        let negFilename = "";
        if (combinedNegatives.length > 0) {
            const negRows = [
                ["Campaign", "Ad Group", "Keyword", "Criterion Type"],
            ];
            for (const kw of combinedNegatives) {
                negRows.push([p.campaign_name, "", kw, "Negative Phrase"]);
            }
            const negCsv = negRows
                .map((r) => r.map(escape).join(","))
                .join("\n");
            const negBlob = new Blob([negCsv], { type: "text/csv;charset=utf-8" });
            const negUrl = URL.createObjectURL(negBlob);
            const negA = document.createElement("a");
            negFilename = `${baseName}_negatives.csv`;
            negA.href = negUrl;
            negA.download = negFilename;
            negA.click();
            URL.revokeObjectURL(negUrl);
        }

        // Emit a synthetic "result" so DoneStep can display CSV summary.
        setPushResult({
            csv_filename: filename,
            negatives_filename: negFilename,
            campaign_name: p.campaign_name,
            ad_groups: p.ad_groups.map((g) => ({
                name: g.name,
                keywords: (g.keywords || []).length,
            })),
            total_keywords: p.ad_groups.reduce(
                (s, g) => s + (g.keywords || []).length,
                0
            ),
            total_negatives: combinedNegatives.length,
        });
        setDoneMode("csv");
        setStep("done");
        toast.success(
            combinedNegatives.length > 0
                ? "Downloaded 2 files — main campaign + negatives (Editor-ready)"
                : "CSV downloaded — import via Google Ads Editor → File → Import CSV"
        );
    };

    // ---- Render ----
    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!v) reset();
                onOpenChange(v);
            }}
        >
            <DialogContent
                data-testid="campaign-push-wizard"
                className="max-w-4xl rounded-none border-2 border-ink p-0 bg-white flex flex-col max-h-[92vh]"
            >
                <DialogHeader className="p-6 border-b-2 border-ink shrink-0">
                    <DialogTitle className="font-display text-2xl font-black tracking-tighter text-ink flex items-center gap-2">
                        <RocketLaunch size={22} weight="bold" className="text-klein" />
                        Push to Google Ads
                    </DialogTitle>
                    <DialogDescription className="text-xs font-mono text-neutral-600 mt-1">
                        Account: <span className="font-bold">{accountName || customerId}</span>
                        {city && (
                            <>
                                {" · City: "}
                                <span className="font-bold">{city}</span>
                            </>
                        )}
                        {" · "}
                        <span className="font-bold text-emerald-700">
                            {targets.length} targets
                        </span>
                        {" · "}
                        <span className="font-bold text-red-700">
                            {negatives.length} negatives
                        </span>
                    </DialogDescription>
                    <Stepper current={step} />
                </DialogHeader>

                <div className="flex-1 overflow-y-auto thin-scroll">
                    {step === "config" && (
                        <ConfigStep
                            businessName={businessName}
                            setBusinessName={setBusinessName}
                            businessType={businessType}
                            setBusinessType={setBusinessType}
                            websiteUrl={websiteUrl}
                            setWebsiteUrl={setWebsiteUrl}
                            dailyBudget={dailyBudget}
                            setDailyBudget={setDailyBudget}
                            competitorText={competitorText}
                            setCompetitorText={setCompetitorText}
                            areasText={areasText}
                            setAreasText={setAreasText}
                            servicesText={servicesText}
                            setServicesText={setServicesText}
                            painPoints={painPoints}
                            setPainPoints={setPainPoints}
                            customNegatives={customNegatives}
                            setCustomNegatives={setCustomNegatives}
                            onSuggestNegatives={handleSuggestNegatives}
                            suggesting={suggesting}
                            suggestedGrouped={suggestedGrouped}
                            combinedNegativesCount={combinedNegatives.length}
                            appendStdNegatives={appendStdNegatives}
                            setAppendStdNegatives={setAppendStdNegatives}
                            city={city}
                            cityGeoId={cityGeoId}
                        />
                    )}
                    {step === "enrich" && enriched && (
                        <EnrichStep
                            enriched={enriched}
                            activeKeywords={activeKeywords}
                            focusThreeWord={focusThreeWord}
                            setFocusThreeWord={setFocusThreeWord}
                            dropKw={dropKw}
                            droppedKws={droppedKws}
                            resetDropped={() => setDroppedKws(new Set())}
                            autoClean={autoClean}
                            setAutoClean={setAutoClean}
                            hygiene={hygiene}
                            rivalBrands={rivalBrands}
                        />
                    )}
                    {step === "plan" && plan && (
                        <PlanStep
                            plan={plan}
                            expandedGroups={expandedGroups}
                            setExpandedGroups={setExpandedGroups}
                        />
                    )}
                    {step === "confirm" && plan && (
                        <ConfirmStep
                            plan={plan}
                            businessName={businessName}
                            dailyBudget={dailyBudget}
                            city={city}
                            country={country}
                            accountName={accountName || customerId}
                            pushError={pushError}
                            onDownloadCsv={downloadEditorCsv}
                            negativesCount={combinedNegatives.length}
                        />
                    )}
                    {step === "done" && pushResult && (
                        <DoneStep result={pushResult} plan={plan} mode={doneMode} />
                    )}
                </div>

                <div className="p-4 border-t-2 border-ink shrink-0 flex items-center gap-2 bg-neutral-50">
                    {step !== "config" && step !== "done" && (
                        <button
                            data-testid="wizard-back"
                            onClick={() => {
                                const idx = STEPS.findIndex((s) => s.key === step);
                                if (idx > 0) setStep(STEPS[idx - 1].key);
                            }}
                            className="brutal-btn h-9 px-3 border border-neutral-400 bg-white text-ink font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1"
                        >
                            <ArrowLeft size={12} weight="bold" /> Back
                        </button>
                    )}
                    <div className="flex-1" />
                    {step === "config" && (
                        <button
                            data-testid="wizard-enrich"
                            onClick={handleEnrich}
                            disabled={enriching}
                            className="brutal-btn h-9 px-4 border border-klein bg-klein text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-2 disabled:opacity-50"
                        >
                            {enriching ? (
                                <ArrowsClockwise size={12} weight="bold" className="animate-spin" />
                            ) : (
                                <Sparkle size={12} weight="bold" />
                            )}
                            Enrich keywords <ArrowRight size={12} weight="bold" />
                        </button>
                    )}
                    {step === "enrich" && (
                        <button
                            data-testid="wizard-plan"
                            onClick={handlePlan}
                            disabled={planning || activeKeywords.length < 3}
                            className="brutal-btn h-9 px-4 border border-klein bg-klein text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-2 disabled:opacity-50"
                        >
                            {planning ? (
                                <ArrowsClockwise size={12} weight="bold" className="animate-spin" />
                            ) : (
                                <Sparkle size={12} weight="bold" />
                            )}
                            Generate plan ({activeKeywords.length} kws) <ArrowRight size={12} weight="bold" />
                        </button>
                    )}
                    {step === "plan" && (
                        <button
                            data-testid="wizard-confirm"
                            onClick={() => setStep("confirm")}
                            className="brutal-btn h-9 px-4 border border-ink bg-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-2"
                        >
                            Review before push <ArrowRight size={12} weight="bold" />
                        </button>
                    )}
                    {step === "confirm" && (
                        <>
                            <button
                                data-testid="wizard-push-secondary"
                                onClick={handlePush}
                                disabled={pushing || !cityGeoId}
                                className="brutal-btn h-9 px-3 border border-neutral-400 bg-white text-neutral-600 hover:bg-neutral-100 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-50"
                                title="Requires Basic access on your Google Ads developer token. Explorer/Test tokens will fail."
                            >
                                {pushing ? (
                                    <ArrowsClockwise size={11} weight="bold" className="animate-spin" />
                                ) : (
                                    <RocketLaunch size={11} weight="bold" />
                                )}
                                Try direct push (needs Basic access)
                            </button>
                            <button
                                data-testid="wizard-download-csv-primary"
                                onClick={downloadEditorCsv}
                                className="brutal-btn h-9 px-4 border border-klein bg-klein text-white hover:bg-klein/90 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-2"
                            >
                                <ArrowSquareOut size={12} weight="bold" />
                                Download Google Ads Editor CSV
                            </button>
                        </>
                    )}
                    {step === "done" && (
                        <button
                            data-testid="wizard-close"
                            onClick={() => onOpenChange(false)}
                            className="brutal-btn h-9 px-4 border border-ink bg-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-2"
                        >
                            <Check size={12} weight="bold" /> Done
                        </button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

// ---- Sub steps ----

const Stepper = ({ current }) => {
    const idx = STEPS.findIndex((s) => s.key === current);
    return (
        <div className="flex items-center gap-1 mt-3 flex-wrap">
            {STEPS.map((s, i) => {
                const done = i < idx;
                const active = i === idx;
                return (
                    <div key={s.key} className="flex items-center gap-1">
                        <span
                            className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest font-bold border ${
                                active
                                    ? "bg-klein text-white border-klein"
                                    : done
                                    ? "bg-ink text-white border-ink"
                                    : "bg-white text-neutral-500 border-neutral-300"
                            }`}
                        >
                            {i + 1}. {s.label}
                        </span>
                        {i < STEPS.length - 1 && (
                            <span className="text-neutral-300 text-[10px]">›</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const Field = ({ label, hint, children }) => (
    <div>
        <label className="mono-label mb-1 block">{label}</label>
        {children}
        {hint && (
            <p className="text-[10px] font-mono text-neutral-500 mt-1">{hint}</p>
        )}
    </div>
);

const ConfigStep = ({
    businessName, setBusinessName,
    businessType, setBusinessType,
    websiteUrl, setWebsiteUrl,
    dailyBudget, setDailyBudget,
    competitorText, setCompetitorText,
    areasText, setAreasText,
    servicesText, setServicesText,
    painPoints, setPainPoints,
    customNegatives, setCustomNegatives,
    onSuggestNegatives, suggesting, suggestedGrouped,
    combinedNegativesCount,
    appendStdNegatives, setAppendStdNegatives,
    city, cityGeoId,
}) => (
    <div className="p-6 space-y-4">
        {!cityGeoId && (
            <div
                data-testid="wizard-no-city-warning"
                className="border border-amber-400 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2"
            >
                <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
                No City selected in Keyword Planner. You can still generate the plan, but
                the push step will need a city geo target — go back and pick one from
                the geo suggest dropdown.
            </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Business name *">
                <input
                    data-testid="wizard-business-name"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="e.g. Metroberry Appliance Care"
                    maxLength={30}
                    className="w-full h-9 text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3"
                />
            </Field>
            <Field label="Business type / core service">
                <input
                    data-testid="wizard-business-type"
                    value={businessType}
                    onChange={(e) => setBusinessType(e.target.value)}
                    placeholder="e.g. washing machine repair"
                    className="w-full h-9 text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3"
                />
            </Field>
            <Field label="Landing page URL *">
                <div className="flex items-center border border-neutral-300 focus-within:border-ink">
                    <LinkIcon size={12} weight="bold" className="mx-2 text-neutral-500 shrink-0" />
                    <input
                        data-testid="wizard-url"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        placeholder="https://client.com/services/washing-machine-repair"
                        className="w-full h-9 text-sm font-mono bg-white focus:outline-none pr-2"
                    />
                </div>
            </Field>
            <Field label="Daily budget *" hint="Account currency">
                <div className="flex items-center border border-neutral-300 focus-within:border-ink">
                    <Money size={12} weight="bold" className="mx-2 text-neutral-500 shrink-0" />
                    <input
                        data-testid="wizard-budget"
                        value={dailyBudget}
                        onChange={(e) => setDailyBudget(e.target.value)}
                        placeholder="20"
                        type="number"
                        min="1"
                        className="w-full h-9 text-sm font-mono bg-white focus:outline-none pr-2"
                    />
                </div>
            </Field>
        </div>

        <Field
            label="Competitor URLs (optional)"
            hint="One per line — we'll scrape titles + H tags for missed keywords"
        >
            <textarea
                data-testid="wizard-competitors"
                value={competitorText}
                onChange={(e) => setCompetitorText(e.target.value)}
                placeholder={"https://competitor1.com\nhttps://competitor2.com"}
                rows={2}
                className="w-full text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3 py-2 resize-none"
            />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
                label="Areas / suburbs served (optional)"
                hint="Comma or newline separated"
            >
                <input
                    data-testid="wizard-areas"
                    value={areasText}
                    onChange={(e) => setAreasText(e.target.value)}
                    placeholder="Whitefield, Koramangala, HSR Layout"
                    className="w-full h-9 text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3"
                />
            </Field>
            <Field
                label="Services offered (optional)"
                hint="Combined with seeds — e.g. 'installation', 'AMC', 'repair'"
            >
                <input
                    data-testid="wizard-services"
                    value={servicesText}
                    onChange={(e) => setServicesText(e.target.value)}
                    placeholder="installation, AMC, gas top-up"
                    className="w-full h-9 text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3"
                />
            </Field>
        </div>

        <Field
            label="Customer pain points (optional)"
            hint="Used for Description #3 — one per line, or a short paragraph"
        >
            <textarea
                data-testid="wizard-pain"
                value={painPoints}
                onChange={(e) => setPainPoints(e.target.value)}
                placeholder={"Overpriced OEM service centres\nNo transparency on parts\nSlow response times"}
                rows={3}
                className="w-full text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3 py-2 resize-none"
            />
        </Field>

        {/* Negatives section */}
        <div className="border-2 border-red-700 bg-red-50/40 p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <div className="font-heading font-black text-red-900 text-base">
                        Negative keywords
                    </div>
                    <div className="text-[11px] font-mono text-red-800 mt-0.5">
                        Combined with Keyword-Planner negatives · exports as a separate Editor CSV
                        {typeof combinedNegativesCount === "number" && (
                            <>
                                {" · "}
                                <span className="font-bold">
                                    {combinedNegativesCount} negative{combinedNegativesCount === 1 ? "" : "s"} so far
                                </span>
                            </>
                        )}
                    </div>
                </div>
                <button
                    data-testid="wizard-suggest-negatives"
                    onClick={onSuggestNegatives}
                    disabled={suggesting}
                    className="brutal-btn h-8 px-3 border border-red-700 bg-red-700 text-white hover:bg-red-800 font-mono uppercase tracking-widest text-[10px] font-bold flex items-center gap-1 disabled:opacity-50"
                >
                    {suggesting ? (
                        <ArrowsClockwise size={11} weight="bold" className="animate-spin" />
                    ) : (
                        <Sparkle size={11} weight="bold" />
                    )}
                    Suggest via AI
                </button>
            </div>

            {Object.keys(suggestedGrouped || {}).length > 0 && (
                <div className="border border-red-300 bg-white p-3 space-y-2">
                    <div className="mono-label text-red-800">
                        AI-suggested (by category) — will be included on export
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 max-h-52 overflow-y-auto thin-scroll">
                        {Object.entries(suggestedGrouped).map(([cat, kws]) => (
                            <div key={cat}>
                                <div className="text-[10px] font-mono uppercase tracking-widest font-bold text-red-900 mb-0.5">
                                    {cat} ({kws.length})
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {kws.map((k) => (
                                        <span
                                            key={k}
                                            className="px-1.5 py-0.5 text-[10px] font-mono bg-neutral-100 border border-neutral-300 text-ink"
                                        >
                                            {k}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <label
                data-testid="wizard-append-std-neg"
                className="border border-red-300 bg-white px-3 py-2 flex items-center gap-2 text-xs font-mono text-red-900 cursor-pointer"
            >
                <input
                    type="checkbox"
                    checked={!!appendStdNegatives}
                    onChange={(e) => setAppendStdNegatives(e.target.checked)}
                    className="accent-red-700"
                />
                <span className="font-bold">Auto-append standard negatives</span>
                <span className="text-red-700 text-[10px]">
                    (amazon, youtube, jobs, review, diy, free, salary, wikipedia + your rival brands)
                </span>
            </label>

            <Field
                label="Your additional negatives"
                hint="Paste your own — comma or newline separated. Merged with the AI list on export."
            >
                <textarea
                    data-testid="wizard-custom-negatives"
                    value={customNegatives}
                    onChange={(e) => setCustomNegatives(e.target.value)}
                    placeholder={"free\ndiy\ntutorial\nyoutube\nsecond hand"}
                    rows={4}
                    className="w-full text-sm font-mono bg-white border border-neutral-300 focus:border-ink focus:outline-none px-3 py-2 resize-none"
                />
            </Field>
        </div>
    </div>
);

const EnrichStep = ({
    enriched,
    activeKeywords,
    focusThreeWord,
    setFocusThreeWord,
    dropKw,
    droppedKws,
    resetDropped,
    autoClean,
    setAutoClean,
    hygiene,
    rivalBrands,
}) => (
    <div className="p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
                <Stat label="Total" value={enriched.total} />
                <Stat label="3-word" value={enriched.three_word_count} highlight />
                <Stat label="Active" value={activeKeywords.length} />
                {droppedKws.size > 0 && (
                    <button
                        onClick={resetDropped}
                        className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 hover:text-ink"
                    >
                        Restore {droppedKws.size} dropped
                    </button>
                )}
            </div>
            <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-xs font-mono" title="Strip rival brands, SKU product codes, and informational queries">
                    <input
                        data-testid="wizard-auto-clean"
                        type="checkbox"
                        checked={autoClean}
                        onChange={(e) => setAutoClean(e.target.checked)}
                        className="accent-klein"
                    />
                    Auto-clean junk
                </label>
                <label className="flex items-center gap-2 text-xs font-mono">
                    <input
                        data-testid="wizard-focus-3word"
                        type="checkbox"
                        checked={focusThreeWord}
                        onChange={(e) => setFocusThreeWord(e.target.checked)}
                        className="accent-klein"
                    />
                    Focus on 3-word keywords only
                </label>
            </div>
        </div>
        {autoClean && hygiene?.dropped?.length > 0 && (
            <div
                data-testid="wizard-hygiene-summary"
                className="border-2 border-red-300 bg-red-50 px-3 py-2 flex items-center flex-wrap gap-3 text-[11px] font-mono"
            >
                <span className="mono-label text-red-900">
                    Auto-cleaned {hygiene.dropped.length} keyword{hygiene.dropped.length === 1 ? "" : "s"}
                </span>
                <span className="text-red-800">·</span>
                {Object.entries(hygiene.reasons)
                    .filter(([, n]) => n > 0)
                    .map(([reason, n]) => (
                        <span
                            key={reason}
                            data-testid={`hygiene-reason-${reason}`}
                            className="text-red-900"
                        >
                            {HYGIENE_REASON_LABELS[reason] || reason}: {n}
                        </span>
                    ))}
                {rivalBrands?.length > 0 && (
                    <span
                        data-testid="wizard-rival-brands"
                        className="ml-auto text-[10px] text-red-700"
                        title={`Rival brands used for filtering: ${rivalBrands.join(", ")}`}
                    >
                        · {rivalBrands.length} rival brand{rivalBrands.length === 1 ? "" : "s"} sourced
                    </span>
                )}
            </div>
        )}
        <div className="flex flex-wrap gap-2">
            {Object.entries(enriched.sources).map(([src, n]) => {
                const b = SOURCE_BADGES[src] || {
                    label: src,
                    cls: "bg-neutral-200 text-ink",
                };
                return (
                    <span
                        key={src}
                        className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest font-bold ${b.cls}`}
                    >
                        {b.label} · {n}
                    </span>
                );
            })}
        </div>
        <div className="border border-neutral-300 max-h-[380px] overflow-y-auto thin-scroll">
            <table className="w-full text-sm">
                <thead className="bg-neutral-100 sticky top-0">
                    <tr>
                        <th className="px-3 py-2 text-left mono-label">Keyword</th>
                        <th className="px-3 py-2 text-right mono-label w-16">Words</th>
                        <th className="px-3 py-2 text-right mono-label w-24">Source</th>
                        <th className="px-3 py-2 w-10"></th>
                    </tr>
                </thead>
                <tbody>
                    {activeKeywords.map((k) => {
                        const b = SOURCE_BADGES[k.source] || {
                            label: k.source,
                            cls: "bg-neutral-200 text-ink",
                        };
                        return (
                            <tr key={k.keyword} className="border-t border-neutral-200 hover:bg-neutral-50">
                                <td className="px-3 py-1.5 font-medium text-ink">{k.keyword}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-neutral-600">
                                    {k.word_count}
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                    <span
                                        className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest font-bold ${b.cls}`}
                                    >
                                        {b.label}
                                    </span>
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                    <button
                                        onClick={() => dropKw(k.keyword)}
                                        className="text-neutral-400 hover:text-red-700"
                                        title="Remove"
                                    >
                                        <Trash size={12} weight="bold" />
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                    {activeKeywords.length === 0 && (
                        <tr>
                            <td colSpan={4} className="px-3 py-6 text-center text-xs text-neutral-500">
                                No keywords match the current filter.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    </div>
);

const PlanStep = ({ plan, expandedGroups, setExpandedGroups }) => {
    const p = plan.plan;
    const toggle = (i) =>
        setExpandedGroups((prev) => {
            const n = new Set(prev);
            if (n.has(i)) n.delete(i);
            else n.add(i);
            return n;
        });
    return (
        <div className="p-6 space-y-4">
            <div className="border border-ink bg-neutral-50 p-3">
                <div className="mono-label">Campaign name</div>
                <div className="font-heading text-lg font-black text-ink">{p.campaign_name}</div>
            </div>
            {p.stats?.truncated && (
                <div
                    data-testid="plan-truncated-warning"
                    className="border-2 border-yellow-500 bg-yellow-50 px-3 py-2 text-[11px] font-mono text-yellow-900"
                >
                    ⚠ Only the top {p.stats.kw_used} of {p.stats.kw_in} keywords were sent to Claude
                    (keeps the plan quick + prevents truncation). Turn on
                    <span className="font-bold"> Auto-clean junk</span> in ENRICH or trim your list
                    further to include everything.
                </div>
            )}
            <div className="flex gap-3 flex-wrap">
                <Stat label="Ad Groups" value={plan.ad_group_count} highlight />
                <Stat label="Keywords" value={plan.keyword_count} />
                <Stat label="× 2 match types" value={plan.keyword_count * 2} />
                <Stat label="Negatives" value={(plan.negatives || []).length} />
            </div>
            <div className="space-y-2">
                {p.ad_groups.map((g, i) => {
                    const open = expandedGroups.has(i);
                    return (
                        <div key={i} className="border border-neutral-300">
                            <button
                                onClick={() => toggle(i)}
                                className="w-full text-left px-4 py-2 flex items-center justify-between bg-neutral-50 hover:bg-neutral-100"
                            >
                                <div>
                                    <div className="font-heading font-bold text-ink">
                                        {g.name}
                                    </div>
                                    <div className="text-[11px] font-mono text-neutral-500 mt-0.5">
                                        {g.theme} · {g.keywords.length} keywords
                                    </div>
                                </div>
                                <CaretDown
                                    size={14}
                                    weight="bold"
                                    className={`transition ${open ? "rotate-180" : ""}`}
                                />
                            </button>
                            {open && (
                                <div className="p-4 space-y-4 bg-white">
                                    <div>
                                        <div className="mono-label mb-1">Headlines (6, ≤ 30 chars)</div>
                                        <div className="flex flex-wrap gap-1">
                                            {g.ad?.headlines?.map((h, hi) => (
                                                <span
                                                    key={hi}
                                                    className="px-2 py-1 text-xs font-mono border border-ink bg-white flex items-center gap-1"
                                                    title={`${h.text.length} chars — tag: ${h.tag}`}
                                                >
                                                    <span className="text-[9px] uppercase tracking-widest text-klein font-bold">
                                                        {h.tag}
                                                    </span>
                                                    <span>{h.text}</span>
                                                    <span className="text-[9px] text-neutral-400">
                                                        ({h.text.length})
                                                    </span>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="mono-label mb-1">Descriptions (3, ≤ 90 chars)</div>
                                        <ol className="text-xs font-mono text-ink space-y-1 list-decimal list-inside">
                                            {g.ad?.descriptions?.map((d, di) => (
                                                <li key={di}>
                                                    {d}{" "}
                                                    <span className="text-[9px] text-neutral-400">
                                                        ({d.length})
                                                    </span>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                    <div>
                                        <div className="mono-label mb-1">Keywords</div>
                                        <div className="flex flex-wrap gap-1">
                                            {g.keywords.map((k) => (
                                                <span
                                                    key={k}
                                                    className="px-2 py-0.5 text-[11px] font-mono bg-neutral-100 border border-neutral-300 text-ink"
                                                >
                                                    {k}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="text-[10px] font-mono text-neutral-500 mt-1">
                                            Each will be created as Phrase + Exact match (2 criteria per keyword)
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ConfirmStep = ({ plan, businessName, dailyBudget, city, country, accountName, pushError, onDownloadCsv, negativesCount }) => {
    const p = plan.plan;
    const totalKws = plan.keyword_count;
    const negCount = typeof negativesCount === "number" ? negativesCount : (plan.negatives || []).length;
    return (
        <div className="p-6 space-y-4">
            {pushError && (
                <div
                    data-testid="wizard-push-error"
                    className="border-2 border-amber-500 bg-amber-50 p-4"
                >
                    <div className="flex items-start gap-2">
                        <Warning size={18} weight="bold" className="text-amber-700 mt-0.5 shrink-0" />
                        <div>
                            <div className="font-heading font-black text-amber-900">
                                Direct push blocked
                            </div>
                            <div className="text-xs font-mono text-amber-900 mt-1 whitespace-pre-wrap">
                                {pushError}
                            </div>
                            <div className="text-[10px] font-mono text-amber-800 mt-2">
                                No problem — click <span className="font-bold">Download Google Ads Editor CSV</span> below.
                                Import via Editor → File → Import → From File. Same result, one manual step.
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="border-2 border-klein bg-klein/5 p-4">
                <div className="flex items-start gap-2">
                    <ArrowSquareOut size={18} weight="bold" className="text-klein mt-0.5" />
                    <div>
                        <div className="font-heading font-black text-ink text-lg">
                            Recommended: download the Google Ads Editor CSV
                        </div>
                        <div className="text-xs font-mono text-neutral-700 mt-1">
                            Direct API push needs a Basic-access developer token from Google. Until that&apos;s
                            approved, use the CSV route — same output, one Editor import. Everything below
                            imports as <span className="font-bold">PAUSED</span> so nothing runs until you enable it.
                        </div>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard icon={<Buildings size={14} weight="bold" />} label="Account">
                    {accountName}
                </SummaryCard>
                <SummaryCard icon={<MapPin size={14} weight="bold" />} label="Location">
                    {city || "—"} · {country}
                </SummaryCard>
                <SummaryCard icon={<Money size={14} weight="bold" />} label="Daily Budget">
                    {dailyBudget}
                </SummaryCard>
                <SummaryCard icon={<Buildings size={14} weight="bold" />} label="Business">
                    {businessName}
                </SummaryCard>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <BigStat label="Campaign" value={1} />
                <BigStat label="Ad Groups" value={plan.ad_group_count} />
                <BigStat label="Keywords" value={`${totalKws} × 2`} sub="Phrase + Exact" />
                <BigStat label="Ads" value={plan.ad_group_count} sub="1 RSA per group" />
            </div>
            <BigStat label="Negatives (separate CSV)" value={negCount} sub="Google Ads Editor format" />
            <div className="border border-neutral-300 p-3">
                <div className="mono-label mb-1">Campaign name</div>
                <div className="font-heading font-bold text-ink">{p.campaign_name}</div>
            </div>
        </div>
    );
};

const DoneStep = ({ result, plan, mode }) => {
    if (mode === "csv") {
        return (
            <div className="p-6 space-y-4">
                <div className="border-2 border-emerald-700 bg-emerald-50 p-4 flex items-start gap-3">
                    <Check size={22} weight="bold" className="text-emerald-700 mt-0.5" />
                    <div>
                        <div className="font-heading font-black text-emerald-900 text-lg">
                            {result.negatives_filename ? "Files downloaded (2)" : "CSV downloaded"}
                        </div>
                        <div className="text-xs font-mono text-emerald-900 mt-1 space-y-0.5">
                            <div>Campaign: <span className="font-bold">{result.csv_filename}</span></div>
                            {result.negatives_filename && (
                                <div>Negatives: <span className="font-bold">{result.negatives_filename}</span></div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="border-2 border-ink bg-white p-4">
                    <div className="font-heading font-black text-ink text-base mb-2">
                        Next steps — import via Google Ads Editor
                    </div>
                    <ol className="text-xs font-mono text-ink space-y-1 list-decimal list-inside">
                        <li>Open Google Ads Editor and sign in to the account.</li>
                        <li>Go to <span className="font-bold">File → Import → From File</span>.</li>
                        <li>Import <span className="font-bold">{result.csv_filename}</span> first — creates the campaign + ad groups + keywords + RSAs (paused).</li>
                        {result.negatives_filename && (
                            <li>Then import <span className="font-bold">{result.negatives_filename}</span> — adds negative phrases to the same campaign.</li>
                        )}
                        <li>Review the changes, click <span className="font-bold">Post</span>. Everything lands PAUSED.</li>
                        <li>In the Ads UI, unpause the campaign when you&apos;re ready to serve.</li>
                    </ol>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <BigStat label="Campaign" value={1} />
                    <BigStat label="Ad Groups" value={result.ad_groups?.length || 0} />
                    <BigStat label="Keywords" value={result.total_keywords || 0} sub="× 2 match types" />
                    <BigStat label="Negatives" value={result.total_negatives || 0} />
                </div>
                {result.ad_groups?.length > 0 && (
                    <div className="border border-neutral-300">
                        <div className="px-3 py-2 bg-neutral-50 mono-label border-b border-neutral-300">
                            Ad groups in this file
                        </div>
                        <table className="w-full text-sm">
                            <tbody>
                                {result.ad_groups.map((g, i) => (
                                    <tr key={i} className="border-t border-neutral-200">
                                        <td className="px-3 py-1.5 font-medium text-ink">{g.name}</td>
                                        <td className="px-3 py-1.5 text-right font-mono text-neutral-600">
                                            {g.keywords} keywords
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }
    // Direct-push success mode.
    return (
    <div className="p-6 space-y-4">
        <div className="border-2 border-emerald-700 bg-emerald-50 p-4 flex items-start gap-3">
            <Check size={22} weight="bold" className="text-emerald-700 mt-0.5" />
            <div>
                <div className="font-heading font-black text-emerald-900 text-lg">
                    Campaign pushed successfully
                </div>
                <div className="text-xs font-mono text-emerald-900 mt-1">
                    All resources were created in a <span className="font-bold">PAUSED</span> state
                    — review inside Google Ads before enabling.
                </div>
            </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <BigStat label="Campaign ID" value={result.campaign_id || "—"} />
            <BigStat label="Ad Groups" value={result.ad_groups?.length || 0} />
            <BigStat
                label="Negatives list"
                value={result.negative_list_size || 0}
            />
            <BigStat label="Status" value="PAUSED" />
        </div>
        {result.deep_link && (
            <a
                data-testid="wizard-deep-link"
                href={result.deep_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 h-9 px-3 border border-klein bg-klein text-white font-mono uppercase tracking-widest text-[10px] font-bold"
            >
                <ArrowSquareOut size={12} weight="bold" /> Open in Google Ads
            </a>
        )}
        {result.ad_groups?.length > 0 && (
            <div className="border border-neutral-300 mt-3">
                <div className="px-3 py-2 bg-neutral-50 mono-label border-b border-neutral-300">
                    Created ad groups
                </div>
                <table className="w-full text-sm">
                    <tbody>
                        {result.ad_groups.map((g) => (
                            <tr key={g.id} className="border-t border-neutral-200">
                                <td className="px-3 py-1.5 font-medium text-ink">{g.name}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-neutral-500">
                                    #{g.id}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-neutral-600">
                                    {g.keywords} keywords
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
    );
};

const Stat = ({ label, value, highlight }) => (
    <div className="border border-neutral-300 bg-white px-3 py-1.5">
        <div className="mono-label text-neutral-500">{label}</div>
        <div
            className={`font-heading text-base font-black ${
                highlight ? "text-klein" : "text-ink"
            }`}
        >
            {value}
        </div>
    </div>
);

const BigStat = ({ label, value, sub }) => (
    <div className="border-2 border-ink bg-white px-3 py-2">
        <div className="mono-label text-neutral-500">{label}</div>
        <div className="font-heading text-xl font-black text-ink leading-tight">
            {value}
        </div>
        {sub && (
            <div className="text-[10px] font-mono text-neutral-500 mt-0.5">{sub}</div>
        )}
    </div>
);

const SummaryCard = ({ icon, label, children }) => (
    <div className="border border-neutral-300 bg-white p-3">
        <div className="mono-label text-neutral-500 flex items-center gap-1 mb-1">
            {icon}
            {label}
        </div>
        <div className="text-sm font-mono text-ink truncate">{children}</div>
    </div>
);
